import { NeedDto, UserDto } from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import { timeFormatter } from '@act/shared/utilities';

function calculateRemainingDays(expiresAt: number): number {
  return timeFormatter.differenceInDaysFromNow(expiresAt);
}

function formatNeedDateLine(need: NeedDto, member?: UserDto) {
  const indentifiedAt = timeFormatter.epochToHumanReadableDate({
    date: need.identifiedAt,
  });

  const remainingDays = calculateRemainingDays(need.expiresAt);

  let durationCopy;

  const isEditable = member && needIsEditable(need, member);

  if (remainingDays >= 0 && isEditable) {
    durationCopy =
      remainingDays > 0 ? `${remainingDays} days remaining` : 'Last day';
  } else {
    durationCopy = 'Closed';
  }

  return `${indentifiedAt} · ${durationCopy}`;
}

type NeedLabel = 'Fulfilled' | 'Identified' | 'Stopped pursuing' | 'Unresolved';

function calculateNeedLabel(need: NeedDto, member: UserDto): NeedLabel {
  if (need.fulfilledAt) {
    return 'Fulfilled';
  }

  if (need.abandonedAt) {
    return 'Stopped pursuing';
  }

  if (
    !need.fulfilledAt &&
    (calculateRemainingDays(need.expiresAt) < 0 ||
      member?.memberStage?.key === 'independence')
  ) {
    return 'Unresolved';
  }

  return 'Identified';
}

function needIsEditable(need: NeedDto, member: UserDto) {
  if (!need || !member?.memberStage) {
    // be conservative and say need is closed if we don't have enough information
    return false;
  }
  if (need.memberId !== member.id) {
    return false;
  }
  const isExpired = need.expiresAt < DateTime.now().toMillis();
  
  // Expired needs are never editable, regardless of status
  if (isExpired) {
    return false;
  }
  
  // Member in independence stage cannot have needs edited
  if (member.memberStage?.key === 'independence') {
    return false;
  }
  
  // Fulfilled needs that are not expired are editable
  if (need.fulfilledAt) {
    return true;
  }
  
  // Non-expired, non-abandoned needs are editable
  return !need.abandonedAt;
}

const syvaNeedsUtils = {
  calculateNeedLabel,
  calculateRemainingDays,
  formatNeedDateLine,
  needIsEditable,
};

export default syvaNeedsUtils;
